import React, { useState, useEffect } from 'react';
import { Card, Typography, Switch, Input, Button, Row, Col, message, Spin, Space, Popconfirm, Image, Alert } from 'antd';
import {
  BellOutlined, SendOutlined, SettingOutlined, QrcodeOutlined,
  WhatsAppOutlined, CheckCircleTwoTone, StopOutlined, ReloadOutlined,
  MessageOutlined
} from '@ant-design/icons';
const { Title, Text, Paragraph } = Typography;

export const AdminNotifications: React.FC<{ token: string; API_URL: string }> = ({ token, API_URL }) => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<any>({
    telegram_bot_token: '',
    telegram_chat_id: '',
    telegram_enabled: false,
    wa_number: '',
    wa_enabled: false,
    daily_report_time: '08:00'
  });
  
  const [waStatus, setWaStatus] = useState<string>('UNKNOWN');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    checkWaStatus();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/notifications/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings({ ...settings, ...data });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const checkWaStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/notifications/waha/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWaStatus(data.status || 'UNKNOWN');
      } else {
        setWaStatus('STOPPED');
      }
    } catch (e) {
      setWaStatus('ERROR');
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/v1/admin/notifications/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      if (!res.ok) throw new Error('Gagal menyimpan');
      message.success('Pengaturan notifikasi berhasil disimpan');
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const testTelegram = async () => {
    try {
      message.loading({ content: 'Mengirim test pesan...', key: 'tgTest' });
      const res = await fetch(`${API_URL}/api/v1/admin/notifications/test-telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token: settings.telegram_bot_token, chatId: settings.telegram_chat_id })
      });
      if (!res.ok) throw new Error('Cek kembali Token & Chat ID');
      message.success({ content: 'Pesan Telegram berhasil terkirim!', key: 'tgTest' });
    } catch (e: any) {
      message.error({ content: e.message, key: 'tgTest' });
    }
  };

  const testWa = async () => {
    try {
      message.loading({ content: 'Mengirim test pesan...', key: 'waTest' });
      const res = await fetch(`${API_URL}/api/v1/admin/notifications/test-wa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ number: settings.wa_number })
      });
      if (!res.ok) throw new Error('Pastikan sesi WA aktif dan nomor tujuan benar (Format: 628xxx)');
      message.success({ content: 'Pesan WhatsApp berhasil terkirim!', key: 'waTest' });
    } catch (e: any) {
      message.error({ content: e.message, key: 'waTest' });
    }
  };

  const startWaSession = async () => {
    try {
      message.loading({ content: 'Memulai sesi WA...', key: 'waSession' });
      await fetch(`${API_URL}/api/v1/admin/notifications/waha/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWaStatus('STARTING');
      message.success({ content: 'Sesi dimulai. Menunggu QR Code...', key: 'waSession' });
      setTimeout(fetchQr, 3000);
    } catch (e) {
      message.error({ content: 'Gagal memulai sesi', key: 'waSession' });
    }
  };

  const fetchQr = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/notifications/waha/qr`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        setQrCode(URL.createObjectURL(blob));
        setWaStatus('SCAN_QR_CODE');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const stopWaSession = async () => {
    try {
      await fetch(`${API_URL}/api/v1/admin/notifications/waha/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWaStatus('STOPPED');
      setQrCode(null);
      message.success('Sesi WA dihentikan');
    } catch (e) {
      message.error('Gagal menghentikan sesi');
    }
  };

  if (loading && !settings.id) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', paddingBottom: 60 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, padding: '16px 24px', background: '#e6fcf5', borderRadius: '12px', border: '2px solid #1a202c' }}>
        <BellOutlined style={{ fontSize: '28px', color: '#0ca678', marginRight: 16 }} />
        <div>
          <Title level={4} style={{ margin: 0, fontWeight: 900 }}>Pengaturan Notifikasi Laporan Harian</Title>
          <Text style={{ color: '#555', fontWeight: 600 }}>Terima ringkasan performa sistem secara otomatis setiap hari via WhatsApp dan Telegram.</Text>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card 
            title={<><MessageOutlined style={{ color: '#0088cc', marginRight: 8 }} /> Telegram Bot</>}
            style={{ border: '2px solid #1a202c', borderRadius: 12, boxShadow: '4px 4px 0 #1a202c', height: '100%' }}
            extra={<Switch checked={settings.telegram_enabled} onChange={v => setSettings({...settings, telegram_enabled: v})} />}
          >
            <div style={{ marginBottom: 16 }}>
              <Text strong>Bot Token</Text>
              <Input 
                placeholder="Misal: 123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11" 
                value={settings.telegram_bot_token}
                onChange={e => setSettings({...settings, telegram_bot_token: e.target.value})}
                style={{ marginTop: 8 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Dapatkan dari @BotFather</Text>
            </div>
            <div style={{ marginBottom: 24 }}>
              <Text strong>Chat ID / Group ID Penerima</Text>
              <Input 
                placeholder="Misal: 987654321 (Pribadi) atau -100123456789 (Grup)" 
                value={settings.telegram_chat_id}
                onChange={e => setSettings({...settings, telegram_chat_id: e.target.value})}
                style={{ marginTop: 8 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Bisa Chat ID pribadi atau ID Grup (awali dengan minus untuk grup)</Text>
            </div>
            <Button type="dashed" icon={<SendOutlined />} onClick={testTelegram} block disabled={!settings.telegram_bot_token || !settings.telegram_chat_id}>
              Kirim Pesan Uji Coba Telegram
            </Button>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card 
            title={<><WhatsAppOutlined style={{ color: '#25D366', marginRight: 8 }} /> WhatsApp Gateway (WAHA)</>}
            style={{ border: '2px solid #1a202c', borderRadius: 12, boxShadow: '4px 4px 0 #1a202c', height: '100%' }}
            extra={<Switch checked={settings.wa_enabled} onChange={v => setSettings({...settings, wa_enabled: v})} />}
          >
            <div style={{ marginBottom: 16 }}>
              <Text strong>Status Koneksi Server: </Text>
              {waStatus === 'WORKING' ? (
                <Text type="success" strong><CheckCircleTwoTone twoToneColor="#52c41a"/> TERHUBUNG</Text>
              ) : waStatus === 'SCAN_QR_CODE' ? (
                <Text type="warning" strong>MENUNGGU SCAN QR</Text>
              ) : (
                <Text type="danger" strong>{waStatus}</Text>
              )}
            </div>

            <Space style={{ marginBottom: 24 }}>
              <Button type="primary" onClick={startWaSession} icon={<QrcodeOutlined />} disabled={waStatus === 'WORKING'}>
                {waStatus === 'SCAN_QR_CODE' ? 'Muat Ulang QR' : 'Mulai Sesi & Scan QR'}
              </Button>
              <Button danger onClick={stopWaSession} icon={<StopOutlined />} disabled={waStatus === 'STOPPED' || waStatus === 'UNKNOWN'}>
                Putus Koneksi
              </Button>
              <Button icon={<ReloadOutlined />} onClick={checkWaStatus} />
            </Space>

            {waStatus === 'SCAN_QR_CODE' && qrCode && (
              <div style={{ textAlign: 'center', marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Scan QR Code ini menggunakan WhatsApp Anda:</Text>
                <Image src={qrCode} width={200} />
              </div>
            )}

            {waStatus === 'WORKING' && (
              <Alert type="success" message="Sistem WhatsApp berhasil terhubung!" showIcon style={{ marginBottom: 16, fontWeight: 600 }} />
            )}

            <div style={{ marginBottom: 24 }}>
              <Text strong>Nomor / Group ID WhatsApp Penerima</Text>
              <Input 
                placeholder="Misal: 628123456789 (Nomor) atau 123456789-987654@g.us (Grup)" 
                value={settings.wa_number}
                onChange={e => setSettings({...settings, wa_number: e.target.value})}
                style={{ marginTop: 8 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Untuk nomor gunakan kode negara (62). Untuk Grup gunakan akhiran <strong>@g.us</strong></Text>
            </div>
            <Button type="dashed" icon={<SendOutlined />} onClick={testWa} block disabled={waStatus !== 'WORKING' || !settings.wa_number}>
              Kirim Pesan Uji Coba WA
            </Button>
          </Card>
        </Col>

        <Col xs={24}>
          <Card style={{ border: '2px solid #1a202c', borderRadius: 12, boxShadow: '4px 4px 0 #1a202c', background: '#f8f9fa' }}>
            <Row align="middle" justify="space-between">
              <Col>
                <Title level={5} style={{ margin: 0 }}><SettingOutlined /> Jadwal Pengiriman Laporan Harian</Title>
                <Text type="secondary">Sistem akan secara otomatis menyusun rekapan hari itu dan mengirimkannya</Text>
              </Col>
              <Col>
                <Space>
                  <Input 
                    type="time" 
                    value={settings.daily_report_time} 
                    onChange={e => setSettings({...settings, daily_report_time: e.target.value})}
                    style={{ width: 150, fontSize: 16, fontWeight: 700 }}
                  />
                  <Button type="primary" size="large" onClick={saveSettings} style={{ fontWeight: 800, background: '#1a202c', borderColor: '#1a202c' }}>
                    SIMPAN PENGATURAN
                  </Button>
                  <Button
                    size="large"
                    loading={monthlyLoading}
                    onClick={async () => {
                      try {
                        setMonthlyLoading(true);
                        const res = await fetch(`${API_URL}/api/v1/admin/notifications/trigger-monthly`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) throw new Error('Gagal mengirim laporan bulanan');
                        message.success('Laporan bulanan berhasil dikirim!');
                      } catch (e: any) {
                        message.error(e.message);
                      } finally {
                        setMonthlyLoading(false);
                      }
                    }}
                    style={{ fontWeight: 700, borderColor: '#1a202c' }}
                  >
                    📅 Kirim Laporan Bulanan
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
